import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAuth } from 'hooks/useAuth';
import { useMemberGroupData } from 'hooks/useMemberGroupData';
import { useToast } from 'hooks/useToast';

import { getRelationshipToToday } from 'libs/utils';

import { SubmitClosing } from 'components/layout/SubmitClosing';
import { FeedbackTool } from 'components/layout/FeedbackTool';
import { SubmitRetro } from 'components/layout/SubmitRetro';
import { RetroResults } from 'components/layout/RetroResults';
import { ValuesImageUpload } from 'components/layout/ValuesImageUpload';
import { ValuesResults } from 'components/layout/ValuesResults';
import { SubmitValues } from 'components/layout/SubmitValues';
import { SubmitRecap } from 'components/layout/SubmitRecap';
import { GroupDetails } from 'components/layout/GroupDetails';
import { SessionDescriptions } from 'legacy/components/SessionDescriptions';
import { RecapDetails } from 'legacy/components/RecapDetails';
import { ProfileContainer } from 'components/layout/ProfileContainer';

import { Button } from 'components/ui/Button';

import { Close } from 'components/svg/Close';

import { Bumper, Card } from 'styles/shared';
import { CloseButtonWrapper, ModalBackground } from 'styles/modal';
import { spacing } from 'styles/theme';

type ModalContent =
    | 'closing'
    | 'feedback'
    | 'retro'
    | 'retro-results'
    | 'values'
    | 'values-results'
    | 'values-submit'
    | 'coach-recap'
    | 'group-details'
    | 'session-descriptions'
    | 'recap-details'
    | 'profile'
    | null;

export const Modal = () => {
    const { userId } = useAuth();
    const { closings, group, retros } = useMemberGroupData();
    const { showSuccessToast, showWarningToast } = useToast();

    const [searchParams, setSearchParams] = useSearchParams();

    const scrollRef = useRef() as React.MutableRefObject<HTMLDivElement>;

    const modalContent = searchParams.get('modalContent') as ModalContent;
    const sessionDate = searchParams.get('sessionDate') as string;
    const retroId = searchParams.get('retroId') as string;
    const valuesId = searchParams.get('valuesId') as string;
    const incompleteRecapId = searchParams.get('incompleteRecapId') as string;
    const groupId = searchParams.get('groupId') as string;
    const groupProgramId = searchParams.get('groupProgramId') as string;
    const submittedRecapId = searchParams.get('submittedRecapId') as string;
    const profileId = searchParams.get('profileId') as string;

    const scrollToTop = useCallback(() => {
        scrollRef.current?.scrollTo({ top: 0 });
    }, []);

    const onCloseModal = useCallback(() => {
        if (modalContent != null) {
            searchParams.delete('modalContent');
        }
        if (sessionDate != null) {
            searchParams.delete('sessionDate');
        }
        if (retroId != null) {
            searchParams.delete('retroId');
        }
        if (valuesId != null) {
            searchParams.delete('valuesId');
        }
        if (incompleteRecapId != null) {
            searchParams.delete('incompleteRecapId');
        }
        if (groupId != null) {
            searchParams.delete('groupId');
        }
        if (groupProgramId != null) {
            searchParams.delete('groupProgramId');
        }
        if (submittedRecapId != null) {
            searchParams.delete('submittedRecapId');
        }
        if (profileId != null) {
            searchParams.delete('profileId');
        }
        setSearchParams(searchParams);
    }, [
        modalContent,
        sessionDate,
        retroId,
        valuesId,
        incompleteRecapId,
        groupId,
        groupProgramId,
        submittedRecapId,
        profileId,
        setSearchParams,
        searchParams,
    ]);

    useEffect(() => {
        if (group == null || modalContent !== 'closing') {
            return;
        }

        if (
            closings.find(
                (closing) =>
                    closing.created_by === userId &&
                    closing.session_date === sessionDate
            ) != null
        ) {
            showSuccessToast(
                `You've already submitted a closing for that session!`
            );
            onCloseModal();
        } else if (
            (sessionDate !== (group?.previous_session_date ?? '') &&
                sessionDate !== (group?.next_session_date ?? '')) ||
            (sessionDate === (group?.previous_session_date ?? '') &&
                getRelationshipToToday(
                    new Date(group?.next_session_date ?? 0)
                ) !== 'upcoming')
        ) {
            showWarningToast(
                'This session closing space is no longer available'
            );
            onCloseModal();
        }
    }, [
        closings,
        group,
        group?.next_session_date,
        group?.previous_session_date,
        modalContent,
        onCloseModal,
        sessionDate,
        showSuccessToast,
        showWarningToast,
        userId,
    ]);

    useEffect(() => {
        if (retros.length === 0 || modalContent !== 'retro') {
            return;
        }

        retros.some((retro) => {
            if (
                getRelationshipToToday(new Date(retro.created_at ?? 0)) ===
                'current'
            ) {
                showWarningToast(
                    `You've already submitted a Grand Retro today`
                );
                return true;
            }
        }, []);
    }, [modalContent, retros, showWarningToast]);

    const sessionNumber = useMemo(() => {
        if (sessionDate === (group?.previous_session_date ?? '')) {
            return (group?.session_counter ?? 1) - 1;
        } else if (sessionDate === (group?.next_session_date ?? '')) {
            return group?.session_counter ?? 1;
        }
        return 0;
    }, [
        group?.next_session_date,
        group?.previous_session_date,
        group?.session_counter,
        sessionDate,
    ]);

    const onMouseDownModalInnerContent = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
        },
        []
    );

    return modalContent == null ? null : (
        <ModalBackground ref={scrollRef} onMouseDown={onCloseModal}>
            <div style={{ width: '100%' }}>
                <Bumper $minHeight={spacing.xxxLarge} $shouldHideOnMobile />
                <Bumper />
                <Card
                    onMouseDown={onMouseDownModalInnerContent}
                    $margin="auto"
                    $width="720px"
                    $mobileWidth="100%"
                    style={{ overflow: 'hidden' }}
                >
                    <CloseButtonWrapper>
                        <Button onClick={onCloseModal} $style="float">
                            <Close />
                        </Button>
                    </CloseButtonWrapper>

                    {modalContent === 'closing' ? (
                        <SubmitClosing
                            sessionNumber={
                                sessionNumber ?? group?.session_counter ?? 0
                            }
                            sessionDate={sessionDate}
                            isPreviousSessionClosing={false}
                            onChangeFormStep={scrollToTop}
                            onClose={onCloseModal}
                        />
                    ) : null}

                    {modalContent === 'feedback' ? (
                        <FeedbackTool
                            onChangeFormStep={scrollToTop}
                            onClose={onCloseModal}
                        />
                    ) : null}

                    {modalContent === 'retro' ? (
                        <SubmitRetro
                            onChangeFormStep={scrollToTop}
                            onClose={onCloseModal}
                        />
                    ) : null}

                    {modalContent === 'retro-results' ? (
                        <RetroResults retroId={retroId} />
                    ) : null}

                    {modalContent === 'values' ? (
                        <ValuesImageUpload onClose={onCloseModal} />
                    ) : null}

                    {modalContent === 'values-results' ? (
                        <ValuesResults valuesId={valuesId} />
                    ) : null}

                    {modalContent === 'values-submit' ? (
                        <SubmitValues
                            onChangeFormStep={scrollToTop}
                            onClose={onCloseModal}
                        />
                    ) : null}

                    {modalContent === 'coach-recap' ? (
                        <SubmitRecap
                            selectedIncompleteRecapId={incompleteRecapId}
                            onChangeFormStep={scrollToTop}
                            onClose={onCloseModal}
                        />
                    ) : null}

                    {modalContent === 'group-details' ? (
                        <GroupDetails groupId={groupId} />
                    ) : null}

                    {modalContent === 'recap-details' ? (
                        <RecapDetails submittedRecapId={submittedRecapId} />
                    ) : null}

                    {modalContent === 'session-descriptions' ? (
                        <SessionDescriptions groupProgramId={groupProgramId} />
                    ) : null}

                    {modalContent === 'profile' ? (
                        <ProfileContainer
                            profileId={profileId}
                            onToggleEdit={scrollToTop}
                        />
                    ) : null}
                </Card>
                <Bumper />
                <Bumper $minHeight={spacing.xxxLarge} />
                <Bumper $minHeight={spacing.medium} $shouldHideOnDesktop />
            </div>
        </ModalBackground>
    );
};
